<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.grupos"
                            @change="handleGrupoChange"
                            default-option-text="Todos"
                            label="Grupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.grupoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.subgrupos"
                            @change="handleSubgrupoChange"
                            default-option-text="Todos"
                            label="Subgrupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.subgrupoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.receitaTipos"
                            @change="handleReceitaTipoChange"
                            default-option-text="Todos"
                            label="Tipo de receita"
                            show-default-option
                            text-field="nome"
                            v-model="model.receitaTipoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.livros"
                            @change="handleLivroChange"
                            default-option-text="Todos"
                            label="Livro"
                            show-default-option
                            text-field="nome"
                            v-model="model.livroId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.livroTipos"
                            @change="handleLivroTipoChange"
                            default-option-text="Todos"
                            label="Tipo de livro"
                            show-default-option
                            text-field="nome"
                            v-model="model.livroTipoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.formasFarmaceuticas"
                            @change="handleFormaFarmaceuticaChange"
                            default-option-text="Todos"
                            label="Forma farmacêutica"
                            show-default-option
                            text-field="nome"
                            v-model="model.formaFarmaceuticaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col
                        v-if="selectedGrupoIsMedicamento"
                        cols="3"
                    >
                        <rkm-form-multiselect
                            :options="form.programasAcoes"
                            :select-limit="null"
                            @update:dropdownClosed="handleProgramaAcaoClosed"
                            label="Programas/Ações"
                            option-name-key="text"
                            v-model="model.programasAcoes"
                            value-field="value"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.status"
                            @change="handleStatusChange"
                            default-option-text="Todos"
                            label="Status"
                            show-default-option
                            v-model="model.status"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.relatorioMaterial"
                            :request-query-string-params="autocompleteQueryStringParams"
                            :search-fields="autocompleteFields"
                            label="Material"
                            text-field="materialDescricao"
                            uppercase
                            v-model="model.material"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            v-if="itens.length > 0"
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
                hide-filters
            />
        </rkm-area>
    </div>
</template>

<script>
// Enums.
import tipoGrupo from '@/enums/cadastro/grupo/tipoGrupo.js'

// Mixins.
import {error, file, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        selectedGrupoIsMedicamento() {
            return this.model.grupoId && this.form.grupos.find(grupo => grupo.id === this.model.grupoId).grupoTipoId === tipoGrupo.MEDICAMENTO
        },
        autocompleteQueryStringParams() {
            return {
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: this.model.livroTipoId,
                formaFarmaceuticaId: this.model.formaFarmaceuticaId,
                programasAcoes: this.model.programasAcoes,
                status: this.model.status,
            }
        },
        autocompleteFields() {
            return [
                {key: 'codigo_material', searchable: true},
                {key: 'descricao', searchable: true},
            ]
        },
        fields() {
            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        optionalColumns() {
            return this.columns.filter(c => !(c.fixed || false))
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.cadastro'),
            impressao: this.getConectaSusRepository('impressao.cadastro'),
            relatorioMaterial: this.getConectaSusRepository('cadastro.relatorioMaterial'),
        }

        this.load()
    },
    data() {
        return {
            columns: [
                {fixed: true, key: 'codigoDescricao', label: 'Código', sortable: true},
                {fixed: true, key: 'materialDescricao', label: 'Material', sortable: true},
                {fixed: true, key: 'grupoNome', label: 'Grupo', sortable: true},
                {fixed: true, key: 'subgrupoNome', label: 'Subgrupo', sortable: true},
                {key: 'receitaTipoNome', label: 'Tipo de receita', sortable: true},
                {key: 'livroNome', label: 'Livro', sortable: true},
                {key: 'livroTipoNome', label: 'Tipo de livro', sortable: true},
                {key: 'formaFarmaceuticaNome', label: 'Forma farmacêutica', sortable: true},
                {key: 'programaAcao', label: 'Programa/Ação', sortable: true},
                {key: 'status', label: 'Status', sortable: true},
                {key: 'possuiLote', label: 'Possui Lote', sortable: true},
                {key: 'materialBnafarCodigo', label: 'Código CATMAT', sortable: true},
                {key: 'materialObmCodigo', label: 'Código OBM', sortable: true},
            ],
            enums: {
                tipoGrupo,
            },
            form: null,
            indeterminate: false,
            itens: [],
            model: null,
            selectedAll: false,
            selectedCols: [],
            sortBy: 'data',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioMaterial.create()
                .then((response) => {
                    this.form = response.data.form
                    this.model = response.data.model
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            const request = {
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: this.model.livroTipoId,
                formaFarmaceuticaId: this.model.formaFarmaceuticaId,
                programasAcoes: this.model.programasAcoes,
                status: this.model.status,
                materialId: this.model.material ? this.model.material.id : null,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.itens = Object.assign(response.data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form.grupos = []
            this.form.subgrupos = []
            this.form.receitaTipos = []
            this.form.livros = []
            this.form.livroTipos = []
            this.form.formasFarmaceuticas = []
            this.form.programasAcoes = []
            this.form.status = []
            this.model.grupoId = null
            this.model.subgrupoId = null
            this.model.receitaTipoId = null
            this.model.livroId = null
            this.model.livroTipoId = null
            this.model.formaFarmaceuticaId = null
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            this.load()

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {
            this.setLoading()

            const request = {
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: this.model.livroTipoId,
                formaFarmaceuticaId: this.model.formaFarmaceuticaId,
                programasAcoes: this.model.programasAcoes,
                status: this.model.status,
                materialId: this.model.material ? this.model.material.id : null,
                columns: this.fields.map(f => ({
                    key: this.camelToSnake(f.key),
                    label: f.label,
                    class: f.class,
                })),
            }

            this.repositories.impressao.relatorioMaterial(this.camelObjectKeysToSnakeRecursive(request))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            const request = {
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: this.model.livroTipoId,
                formaFarmaceuticaId: this.model.formaFarmaceuticaId,
                programasAcoes: this.model.programasAcoes,
                status: this.model.status,
                materialId: this.model.material ? this.model.material.id : null,
                columns: this.fields.map(f => ({
                    key: this.camelToSnake(f.key),
                    label: f.label,
                    class: f.class,
                })),
            }

            this.repositories.exportacao.relatorioMaterial(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        handleGrupoChange(value) {
            this.form.subgrupos = []
            this.form.receitaTipos = []
            this.form.livros = []
            this.form.livroTipos = []
            this.form.formasFarmaceuticas = []
            this.form.programasAcoes = []
            this.form.status = []
            this.model.subgrupoId = null
            this.model.receitaTipoId = null
            this.model.livroId = null
            this.model.livroTipoId = null
            this.model.formaFarmaceuticaId = null
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'subgrupos',
                grupoId: value,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.subgrupos = Object.assign(response.data.subgrupos)
                    this.form.receitaTipos = Object.assign(response.data.receitaTipos)
                    this.form.livros = Object.assign(response.data.livros)
                    this.form.livroTipos = Object.assign(response.data.livroTipos)
                    this.form.formasFarmaceuticas = Object.assign(response.data.formasFarmaceuticas)
                    this.form.programasAcoes = Object.assign(response.data.programasAcoes)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleSubgrupoChange(value) {
            this.form.receitaTipos = []
            this.form.livros = []
            this.form.livroTipos = []
            this.form.formasFarmaceuticas = []
            this.form.programasAcoes = []
            this.form.status = []
            this.model.receitaTipoId = null
            this.model.livroId = null
            this.model.livroTipoId = null
            this.model.formaFarmaceuticaId = null
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'receita-tipos',
                grupoId: this.model.grupoId,
                subgrupoId: value,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.receitaTipos = Object.assign(response.data.receitaTipos)
                    this.form.livros = Object.assign(response.data.livros)
                    this.form.livroTipos = Object.assign(response.data.livroTipos)
                    this.form.formasFarmaceuticas = Object.assign(response.data.formasFarmaceuticas)
                    this.form.programasAcoes = Object.assign(response.data.programasAcoes)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleReceitaTipoChange(value) {
            this.form.livros = []
            this.form.livroTipos = []
            this.form.formasFarmaceuticas = []
            this.form.programasAcoes = []
            this.form.status = []
            this.model.livroId = null
            this.model.livroTipoId = null
            this.model.formaFarmaceuticaId = null
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'livros',
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: value,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.livros = Object.assign(response.data.livros)
                    this.form.livroTipos = Object.assign(response.data.livroTipos)
                    this.form.formasFarmaceuticas = Object.assign(response.data.formasFarmaceuticas)
                    this.form.programasAcoes = Object.assign(response.data.programasAcoes)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleLivroChange(value) {
            this.form.livroTipos = []
            this.form.formasFarmaceuticas = []
            this.form.programasAcoes = []
            this.form.status = []
            this.model.livroTipoId = null
            this.model.formaFarmaceuticaId = null
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'livro-tipos',
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: value,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.livroTipos = Object.assign(response.data.livroTipos)
                    this.form.formasFarmaceuticas = Object.assign(response.data.formasFarmaceuticas)
                    this.form.programasAcoes = Object.assign(response.data.programasAcoes)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleLivroTipoChange(value) {
            this.form.formasFarmaceuticas = []
            this.form.programasAcoes = []
            this.form.status = []
            this.model.formaFarmaceuticaId = null
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'formas-farmaceuticas',
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: value,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.formasFarmaceuticas = Object.assign(response.data.formasFarmaceuticas)
                    this.form.programasAcoes = Object.assign(response.data.programasAcoes)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleFormaFarmaceuticaChange(value) {
            this.form.programasAcoes = []
            this.form.status = []
            this.model.programasAcoes = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'programas-acoes',
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: this.model.livroTipoId,
                formaFarmaceuticaId: value,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.programasAcoes = Object.assign(response.data.programasAcoes)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleProgramaAcaoClosed() {
            this.form.status = []
            this.model.status = null
            this.model.material = null

            this.itens = []

            const request = {
                listar: 'status',
                grupoId: this.model.grupoId,
                subgrupoId: this.model.subgrupoId,
                receitaTipoId: this.model.receitaTipoId,
                livroId: this.model.livroId,
                livroTipoId: this.model.livroTipoId,
                formaFarmaceuticaId: this.model.formaFarmaceuticaId,
                programasAcoes: this.model.programasAcoes,
            }

            this.setLoading()

            this.repositories.relatorioMaterial.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.form.status = Object.assign(response.data.status))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleStatusChange() {
            this.model.material = null
            this.itens = []
        },
        selectAll(selected) {
            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
    },
    mixins: [error, file, repository, stringCase],
    watch: {
        selectedCols(newValue) {
            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    },
}
</script>